import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogActions,
  IconButton,
  DialogContent,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { listRestaurantInvoiceDetails, updateRestaurantInvoiceStatus } from "../../../store/actions";
import { PDFViewer } from "@react-pdf/renderer";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

import InvoicePDF from "./InvoicePDF";
import LinkBookingInvoiceButton from "./LinkBookingInvoiceButton";

const RestaurantBillDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  // ✅ Get data from Redux
  const { restaurantInvoicesDetails, hotelProfile, isLoading } = useSelector(
    (state) => ({
      restaurantInvoicesDetails: state.admin.restaurantInvoicesDetails,
      hotelProfile: state.registration.hotelProfile,
      isLoading: state.loading,
    })
  );

  const [bill, setBill] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const pdfData = useMemo(() => {
    return {
      restaurantInvoicesDetails,
      hotelDetails: {
        name: hotelProfile?.name || "",
        address: hotelProfile?.address || "",
        phone: hotelProfile?.phone || "",
        mobile: hotelProfile?.mobile || "",
        email: hotelProfile?.email || "",
        gstin: hotelProfile?.gstin || "",
        logo: hotelProfile?.logo || "",
        bank_details: hotelProfile?.bank_details || {},
        cin: hotelProfile?.cin || "",
        pan: hotelProfile?.pan || "",
      },
    };
  }, [restaurantInvoicesDetails, hotelProfile]);

  // ✅ Fetch details on mount
  useEffect(() => {
    if (id) dispatch(listRestaurantInvoiceDetails({ id }));
  }, [id, dispatch]);

  // ✅ When Redux updates
  useEffect(() => {
    if (restaurantInvoicesDetails) {
      setBill(restaurantInvoicesDetails);
    }
  }, [restaurantInvoicesDetails]);

  if (isLoading || !bill) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }
  // Convert the UNIX timestamp to readable date
  const formattedDate = new Date(bill.inv_date * 1000).toLocaleString();

  const handleMarkAsPaid = async () => {
    try {
      await dispatch(
        updateRestaurantInvoiceStatus({ id: bill._id, status: "paid" })
      );
      await dispatch(listRestaurantInvoiceDetails({ id })); // reload fresh data
    } catch (err) {
      console.error("Error marking invoice as paid:", err);
    }
  };

  const handleUpdatePaid = async () => {
    try {
      // set status back to "unpaid"
      await dispatch(
        updateRestaurantInvoiceStatus({ id: bill._id, status: "unpaid" })
      );
      await dispatch(listRestaurantInvoiceDetails({ id })); // reload fresh data
    } catch (err) {
      console.error("Error setting invoice to unpaid:", err);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh" }}>
      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          borderRadius: 2,
          bgcolor: colors.primary[400],
        }}
      >
        <Stack spacing={3}>
          {/* Header Actions */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: "1px solid",
              borderColor: colors.grey[700],
              pb: 1,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Restaurant Invoice
            </Typography>

            <Stack direction="row" spacing={1}>
              {!bill?.booking_id?.trim() ? (
                <LinkBookingInvoiceButton colors={colors} />
              ) : (
                <button
                  color="secondary"
                  variant="outlined"
                  onClick={() =>
                    navigate(`/billing/bookings/${bill.booking_id}`)
                  }
                >
                  Go to Booking Invoice
                </button>
              )}

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              {/* <Grid item> */}
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => setShowPreview(true)}
              >
                Preview PDF
              </Button>
              {/* </Grid */}
            </Stack>
          </Stack>

          {/* Invoice Details */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Invoice No
              </Typography>
              <Typography fontWeight={600}>{bill?.inv_no}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Date & Time
              </Typography>
              <Typography fontWeight={600}>{formattedDate}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Customer Name
              </Typography>
              <Typography fontWeight={600}>{bill?.customer_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Mobile
              </Typography>
              <Typography fontWeight={600}>
                {bill?.customer_mobile || "—"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Payment Status
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={bill?.pmt_status?.toUpperCase()}
                  color={bill?.pmt_status === "paid" ? "success" : "warning"}
                  size="small"
                />

                {bill?.pmt_status !== "paid" ? (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleMarkAsPaid}
                  >
                    Mark as Paid
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleUpdatePaid}
                  >
                    Set as Unpaid
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          {/* Ordered Items */}
          <Box>
            <Typography variant="h6" mb={1}>
              Ordered Items
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: colors.blueAccent[700] }}>
                  <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Qty
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Rate (₹)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Amount (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bill?.items?.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{item?.rate?.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {(item?.amount || item?.rate * item?.quantity)?.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Totals Section */}
          <Box textAlign="right" sx={{ mt: 2 }}>
            <Typography>Subtotal: ₹{bill?.amount?.toFixed(2)}</Typography>
            <Typography>SGST (2.5%): ₹{bill?.sgst?.toFixed(2)}</Typography>
            <Typography>CGST (2.5%): ₹{bill?.cgst?.toFixed(2)}</Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color="secondary"
              sx={{ mt: 1 }}
            >
              Grand Total: ₹{bill?.total?.toFixed(2)}
            </Typography>
          </Box>

          {/* Remarks (if any) */}
          {bill?.remarks && bill?.remarks.trim() !== "" && (
            <>
              <Divider />
              <Typography variant="subtitle1">
                <b>Remarks:</b> {bill?.remarks}
              </Typography>
            </>
          )}
        </Stack>
      </Paper>
      <Dialog
        maxWidth="lg"
        fullWidth
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <DialogActions sx={{ justifyContent: "space-between", p: "8px 24px" }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, height: "100vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF data={pdfData} />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RestaurantBillDetailPage;
